import React, {Fragment, useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Media from 'react-media';
import {
  Link, ThemeContext, Avatar, Button, CoinsPackModal,
} from '..';
import silverCoinImage from '../../../assets/yakcoin_silver.png';
import goldCoinImage from '../../../assets/yakcoin_gold.png';

const Sidebar = (props) => {
  const modalNodeRef = useRef(null);
  const [isCoinPacksModalOpen, setCoinPacksModalOpen] = useState(false);

  const onClose = (e) => {
    const {sidebarToggle} = props;
    const isSidebar = e.target.closest('.sidebar__wrapper');

    if (
      isCoinPacksModalOpen
      || !modalNodeRef.current
      || modalNodeRef.current.contains(e.target) || isSidebar
    ) {
      return;
    }

    sidebarToggle({status: false});
  };

  const addCloseClickListeners = () => {
    document.addEventListener('touchstart', onClose, false);
    document.addEventListener('click', onClose, false);
  };
  const removeCloseClickListeners = () => {
    document.removeEventListener('touchstart', onClose);
    document.removeEventListener('click', onClose);
  };

  const onCoinPacksModalClose = () => {
    const {sidebarToggle} = props;
    addCloseClickListeners();
    sidebarToggle({status: false});
    setCoinPacksModalOpen(false);
  };

  const onCoinPacksModalOpen = () => {
    removeCloseClickListeners();
    setCoinPacksModalOpen(true);
  };

  useEffect(() => {
    addCloseClickListeners();

    return () => removeCloseClickListeners();
  }, []);

  const onHelpClick = (e) => {
    e.preventDefault();

    const {sidebarToggle, helpToggle} = props;

    sidebarToggle({status: false});
    helpToggle({status: true});
  };

  const onSettingsClick = () => {
    const {setMediaSettings} = props;

    setMediaSettings({visible: true});
  };

  const hideSidebar = () => {
    const {sidebarToggle} = props;

    sidebarToggle({status: false});
  };

  const rendeProfile = (user, styles) => {
    const {
      application: {ableToSendGiftsForFree},
      coins: {gold, silver},
    } = props;
    const {image, fullName, firstName} = user;

    return (
      <Fragment>
        <span className="sidebar__profile-ava">
          <Avatar image={image} imageWidth={180} fullName={fullName} iconSize="36"/>
        </span>
        <div className="sidebar__profile-panel">
          <div className="sidebar__profile-username">
            <div className="sidebar__profile-username-title" style={styles}>{firstName}</div>
            <div className="coins">
              {!ableToSendGiftsForFree && (
                <div className="flex">
                  <span>YakSilver</span>
                  <img className="coins-popper__icon" src={silverCoinImage} height="20" alt=""/>
                  <span>{silver}</span>
                </div>
              )}
              <div className="flex">
                <span>YakGold</span>
                <img className="coins-popper__icon" src={goldCoinImage} height="20" alt=""/>
                <span>{gold}</span>
              </div>
            </div>
          </div>
          <Link className="sidebar__profile_icon" to="/profile" icon="cog" iconSize="36"/>
          {!ableToSendGiftsForFree && (
            <Button
              className="sidebar__buy-more"
              text="Buy More YakSilver"
              handler={onCoinPacksModalOpen}
            />
          )}
        </div>
      </Fragment>
    );
  };

  const {
    application: {leaderboard, treasureChest},
    location: {pathname},
    onLogout,
    updateUserCoins,
    user,
    router,
  } = props;

  const isLobby = pathname === '/lobby';
  const isLeaderboard = pathname === '/leaderboard';
  const isTreasureChest = pathname === '/treasure-chest';
  const currenLocation = router.location.pathname;
  const isGameLocation = currenLocation.indexOf('/game') !== -1;

  return (
    <ThemeContext.Consumer>
      {(template) => {
        const styles = {
          color: template.primaryColor,
        };
        const paymentPacks = Object
          .keys(template.paymentPacks)
          .map(key => template.paymentPacks[key]);

        return (
          <div className="sidebar">
            <div className="sidebar__wrapper" ref={modalNodeRef}>
              <div className="sidebar__profile">
                {rendeProfile(user, styles)}
              </div>
              <ul className="sidebar__list">
                <li className={classnames({active: isLobby})}>
                  <Link to="/lobby" text="LOBBY"/>
                </li>
                {leaderboard && (
                  <li className={classnames({active: isLeaderboard})}>
                    <Link
                      callback="hideSidebar"
                      to="/leaderboard"
                      text="LEADERBOARD"
                      renderInPanel={isGameLocation}
                      panelMethod="toggleLeaderboard"
                      hideSidebar={hideSidebar}
                    />
                  </li>
                )}
                {treasureChest && (
                  <li className={classnames({active: isTreasureChest})}>
                    <Link
                      callback="hideSidebar"
                      to="/treasure-chest"
                      text="TREASURE CHEST"
                      renderInPanel={isGameLocation}
                      panelMethod="toggleTreasureChest"
                      hideSidebar={hideSidebar}
                    />
                  </li>
                )}
                <li>
                  <Link to="/signout" text="SIGN OUT" onClick={onLogout}/>
                </li>
                <Media query={{maxWidth: 700}}>
                  {
                    isGameLocation && (
                      <li>
                        <a to="/#" style={styles} onClick={onSettingsClick}>SETTINGS</a>
                      </li>
                    )
                  }
                </Media>
                <li>
                  <a to="/#" style={styles} onClick={onHelpClick}>HELP</a>
                </li>
              </ul>
              <div className="sidebar__close" style={styles} onClick={onClose}>×</div>
            </div>
            {paymentPacks && (
              <CoinsPackModal
                isModalOpened={isCoinPacksModalOpen}
                onModalClose={onCoinPacksModalClose}
                paymentPacks={paymentPacks}
                updateUserCoins={updateUserCoins}
              />
            )}
          </div>
        );
      }}
    </ThemeContext.Consumer>
  );
};

Sidebar.propTypes = {
  coins: PropTypes.shape({
    gold: PropTypes.number.isRequired,
    silver: PropTypes.number.isRequired,
  }).isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  user: PropTypes.shape({
    image: PropTypes.string,
    fullName: PropTypes.string,
  }).isRequired,
  application: PropTypes.shape({
    ableToSendGiftsForFree: PropTypes.bool,
    leaderboard: PropTypes.bool,
    treasureChest: PropTypes.bool,
  }).isRequired,
  setMediaSettings: PropTypes.func.isRequired,
  sidebarToggle: PropTypes.func.isRequired,
  helpToggle: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  updateUserCoins: PropTypes.func.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default Sidebar;
